import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useT, LANGS } from '../lib/i18n';
import RecentReflections from '../components/RecentReflections';
import BookDownloadButton from '../components/BookDownloadButton';

export default function Settings() {
  const { t, lang, setLang } = useT();
  const [seconds, setSeconds] = useState(() => parseInt(localStorage.getItem('ctl_friction_seconds') || '8', 10));
  const [confirming, setConfirming] = useState(false);
  const [status, setStatus] = useState('');

  const saveSeconds = (v) => {
    const n = Math.max(3, Math.min(20, parseInt(v, 10) || 8));
    setSeconds(n);
    localStorage.setItem('ctl_friction_seconds', String(n));
  };

  const deleteAll = async () => {
    setStatus('');
    try {
      await api.delete('/data/all');
      setStatus(t('settings.deleteOk'));
      setConfirming(false);
    } catch (_) {
      setStatus(t('settings.deleteFail'));
    }
  };

  return (
    <div className="min-h-screen flex flex-col fade-in" data-testid="settings-screen">
      <header className="w-full px-4 sm:px-10 lg:px-16 pt-6 sm:pt-10 lg:pt-12 pb-6 sm:pb-8 grid grid-cols-3 items-center gap-2">
        <Link
          to="/"
          className="justify-self-start text-[0.7rem] sm:text-xs tracking-[0.18em] uppercase text-[#000000] hover:opacity-60 transition-opacity duration-300 font-pressura"
          style={{ fontWeight: 700 }}
          data-testid="settings-back-link"
        >
          {t('nav.back')}
        </Link>
        <span aria-hidden="true" className="justify-self-center" />
        <span
          className="justify-self-end text-[0.7rem] sm:text-xs tracking-[0.18em] uppercase text-[#000000] opacity-60 font-pressura"
          style={{ fontWeight: 700 }}
          data-testid="settings-page-label"
        >
          {t('settings.title')}
        </span>
      </header>

      <main className="flex-1 py-16">
        <div className="ctl-container w-full space-y-16">
          <section data-testid="settings-language">
            <div className="ctl-eyebrow mb-3">{t('settings.langTitle')}</div>
            <p className="ctl-helper text-sm mb-6">{t('settings.langHelp')}</p>
            <div className="flex flex-wrap gap-3">
              {LANGS.map(l => (
                <button
                  key={l.code}
                  type="button"
                  className={`ctl-chip ${lang === l.code ? 'ctl-chip-active' : ''}`}
                  onClick={() => setLang(l.code)}
                  data-testid={`settings-lang-${l.code}`}
                >
                  {l.label}
                </button>
              ))}
            </div>
          </section>

          <section>
            <div className="ctl-eyebrow mb-3">{t('settings.pauseTitle')}</div>
            <p className="ctl-helper text-sm mb-6">{t('settings.pauseHelp')}</p>
            <div className="flex items-center gap-4">
              <input
                type="range"
                min="3"
                max="20"
                value={seconds}
                onChange={(e) => saveSeconds(e.target.value)}
                className="flex-1 accent-[#2A2724]"
                data-testid="settings-friction-range"
              />
              <span className="font-display text-2xl w-12 text-right" data-testid="settings-friction-value">{seconds}s</span>
            </div>
          </section>

          <section data-testid="settings-book">
            <div className="ctl-eyebrow mb-3">{t('settings.bookTitle')}</div>
            <p className="ctl-helper text-sm mb-6">{t('settings.bookHelp')}</p>
            <BookDownloadButton />
          </section>

          <RecentReflections />

          <section>
            <div className="ctl-eyebrow mb-3">{t('settings.dataTitle')}</div>
            <p className="ctl-helper text-sm mb-3">{t('settings.dataPara')}</p>
            <p className="ctl-helper text-sm mb-6">{t('settings.deleteHelp')}</p>
            {!confirming ? (
              <button onClick={() => setConfirming(true)} className="ctl-btn" data-testid="settings-delete-btn">
                {t('settings.deleteBtn')}
              </button>
            ) : (
              <div className="space-y-4">
                <p>{t('settings.deleteWarn')}</p>
                <div className="flex gap-4">
                  <button onClick={deleteAll} className="ctl-btn" data-testid="settings-delete-confirm-btn">
                    {t('settings.deleteYes')}
                  </button>
                  <button onClick={() => setConfirming(false)} className="ctl-btn-quiet" data-testid="settings-delete-cancel-btn">
                    {t('settings.deleteCancel')}
                  </button>
                </div>
              </div>
            )}
            {status && <p className="ctl-helper text-sm mt-4" data-testid="settings-status">{status}</p>}
          </section>
        </div>
      </main>
    </div>
  );
}
